import {
  UTCformatDateInPlan,
  UTCFormatDateShort,
  getProgramDatesRange,
} from '../../utils/date'

/**
 * Temporal status from programme dates (same rules as list table).
 * @returns {{ key: 'soon'|'run'|'done', label: string }}
 */
export const getTemporalStatus = (quest) => {
  const { start, end } = getProgramDatesRange(quest)
  const currentDate = UTCformatDateInPlan()
  const startFmt = UTCformatDateInPlan(start)
  const endFmt = UTCformatDateInPlan(end)
  if (currentDate < startFmt || start == null || end == null) {
    return { key: 'soon', label: 'Upcoming' }
  }
  if (currentDate >= startFmt && currentDate <= endFmt) {
    return { key: 'run', label: 'In progress' }
  }
  return { key: 'done', label: 'Completed' }
}

export const isQuestPublished = (quest) =>
  !!(quest?.isPublished || quest?.published)

/**
 * List / copy-picker status — same rules as Active & Archive tables.
 * Draft when unpublished; otherwise temporal from programme dates.
 * @returns {{ key: 'draft'|'soon'|'run'|'done', label: string, variant: string, chipClass: string }}
 */
export const getListStatusMeta = (quest) => {
  if (!isQuestPublished(quest)) {
    return {
      key: 'draft',
      label: 'Draft',
      variant: 'draft',
      chipClass: 'badge-draft',
    }
  }
  const temporal = getTemporalStatus(quest)
  if (temporal.key === 'run') {
    return {
      key: 'run',
      label: 'In progress',
      variant: 'steel',
      chipClass: 'badge-run',
    }
  }
  if (temporal.key === 'done') {
    return {
      key: 'done',
      label: 'Completed',
      variant: 'gray',
      chipClass: 'badge-done',
    }
  }
  return {
    key: 'soon',
    label: 'Upcoming',
    variant: 'amber',
    chipClass: 'badge-soon',
  }
}

/**
 * Normalize API readiness into { prosperity, enrollment, prep }.
 * Supports list shape:
 *   { contentIssues, enrollmentIssues, participantsPrepIssues }
 * and nested shape:
 *   { prosperity/enrollment/prep: { ok } | { issues } }
 */
export const normalizeReadiness = (readiness) => {
  if (!readiness || typeof readiness !== 'object') {
    return null
  }

  const hasListShape =
    'contentIssues' in readiness ||
    'enrollmentIssues' in readiness ||
    'participantsPrepIssues' in readiness

  if (hasListShape) {
    const toItem = (value) => {
      const count = Number(value)
      if (Number.isNaN(count) || count <= 0) {
        return { ok: true, issues: 0 }
      }
      return { ok: false, issues: count }
    }
    return {
      prosperity: toItem(readiness.contentIssues),
      enrollment: toItem(readiness.enrollmentIssues),
      prep: toItem(readiness.participantsPrepIssues),
    }
  }

  if (readiness.prosperity || readiness.enrollment || readiness.prep) {
    return readiness
  }

  return null
}

/** Map overview `questStatus` → product scenario key. */
const QUEST_STATUS_TO_SCENARIO = {
  draft: 'draft',
  upcoming: 'upcoming',
  ongoing: 'live',
  completed: 'completed',
}

/**
 * Product lifecycle scenario for Overview / chrome.
 * Prefer overview.experience.questStatus when present.
 * draft | copied | upcoming | live | completed | new
 */
export const getExperienceScenario = (
  quest,
  { isNewQuest = false, isNewlyCopied = false, overview = null } = {},
) => {
  if (isNewQuest || (!quest?._id && !overview?.experience?._id)) {
    return 'new'
  }

  const overviewStatus = overview?.experience?.questStatus
  if (overviewStatus && QUEST_STATUS_TO_SCENARIO[overviewStatus]) {
    const mapped = QUEST_STATUS_TO_SCENARIO[overviewStatus]
    if (mapped === 'draft' && isNewlyCopied) {
      return 'copied'
    }
    return mapped
  }

  if (!isQuestPublished(quest)) {
    return isNewlyCopied ? 'copied' : 'draft'
  }
  const temporal = getTemporalStatus(quest)
  if (temporal.key === 'done') {
    return 'completed'
  }
  if (temporal.key === 'run') {
    return 'live'
  }
  return 'upcoming'
}

export const getStatusBadgeMeta = (
  quest,
  { isNewQuest = false, isNewlyCopied = false, overview = null } = {},
) => {
  const scenario = getExperienceScenario(quest, {
    isNewQuest,
    isNewlyCopied,
    overview,
  })
  if (scenario === 'new' || scenario === 'draft' || scenario === 'copied') {
    return {
      scenario,
      chips: [{ label: 'Draft', className: 'badge-draft' }],
      publishLabel: 'Publish',
      publishDisabled: scenario === 'new' || scenario === 'copied',
      manageEnabled: false,
      headerStatus: 'Draft',
    }
  }
  if (scenario === 'completed') {
    return {
      scenario,
      chips: [{ label: 'Completed', className: 'badge-done' }],
      publishLabel: 'Completed',
      publishDisabled: true,
      manageEnabled: true,
      headerStatus: 'Completed',
    }
  }
  const temporalLabel = scenario === 'live' ? 'In progress' : 'Upcoming'
  return {
    scenario,
    chips: [
      {
        label: `Published · ${temporalLabel}`,
        className: scenario === 'live' ? 'badge-run' : 'badge-soon',
      },
    ],
    publishLabel: 'Published',
    publishDisabled: false,
    manageEnabled: true,
    headerStatus: `Published · ${temporalLabel}`,
  }
}

const readinessItem = (r, key) => {
  const normalized = normalizeReadiness(r) || r
  if (!normalized || typeof normalized !== 'object') {
    return null
  }
  return normalized[key] || null
}

const pillFromItem = (item, okLabel, warnLabel, laterLabel) => {
  if (!item) {
    return {
      text: laterLabel || warnLabel,
      tone: 'warn',
      issues: null,
      ok: false,
    }
  }
  if (item.ok === true || item.complete === true) {
    return { text: okLabel, tone: 'ok', issues: 0, ok: true }
  }
  const n = item.issues ?? item.count ?? item.missing
  if (n != null && Number(n) > 0) {
    return {
      text: `${warnLabel} · ${n} issue${Number(n) === 1 ? '' : 's'}`,
      tone: 'warn',
      issues: Number(n),
      ok: false,
    }
  }
  return { text: warnLabel, tone: 'warn', issues: null, ok: false }
}

/**
 * Modebar readiness pills (Prosperity / Enrollment / Departure).
 * Prefer overview.readiness when provided; prep is absent from overview API
 * so published experiences fall back to "Departure · review".
 */
/** Prefer overview readiness; fill prep from quest when overview omits it. */
const resolveReadiness = (quest, overviewReadiness) => {
  const fromOverview = normalizeReadiness(overviewReadiness)
  const fromQuest = normalizeReadiness(quest?.readiness)
  if (!fromOverview && !fromQuest) {
    return null
  }
  if (!fromOverview) {
    return fromQuest
  }
  if (!fromQuest) {
    return fromOverview
  }
  return {
    prosperity: fromOverview.prosperity || fromQuest.prosperity,
    enrollment: fromOverview.enrollment || fromQuest.enrollment,
    prep: fromOverview.prep || fromQuest.prep,
  }
}

export const getModebarPills = (quest, scenario, overviewReadiness) => {
  const r = resolveReadiness(quest, overviewReadiness)
  if (scenario === 'completed') {
    return [
      { key: 'prosperity', text: 'Prosperity page · retained', tone: 'ok' },
      { key: 'enrollment', text: 'Enrollment · closed', tone: 'ok' },
      { key: 'prep', text: 'Trip · completed', tone: 'ok' },
    ]
  }
  if (scenario === 'new') {
    return [
      { key: 'prosperity', text: 'Prosperity · not started', tone: 'warn' },
      { key: 'enrollment', text: 'Enrollment · later', tone: 'warn' },
      { key: 'prep', text: 'Departure · later', tone: 'warn' },
    ]
  }
  if (scenario === 'draft' || scenario === 'copied') {
    const prosperity = readinessItem(r, 'prosperity')
    const enrollment = readinessItem(r, 'enrollment')
    if (scenario === 'copied') {
      return [
        { key: 'prosperity', text: 'Prosperity · copied', tone: 'ok' },
        { key: 'enrollment', text: 'Enrollment · copied', tone: 'ok' },
        { key: 'prep', text: 'Dates · review', tone: 'warn' },
      ]
    }
    const prosperityPill = pillFromItem(
      prosperity,
      'Prosperity ✓',
      'Prosperity',
      'Prosperity · issues',
    )
    const enrollmentPill = pillFromItem(
      enrollment,
      'Enrollment ✓',
      'Enrollment',
      'Enrollment · issues',
    )
    return [
      { key: 'prosperity', ...prosperityPill },
      { key: 'enrollment', ...enrollmentPill },
      {
        key: 'prep',
        text: 'Departure · later',
        tone: 'warn',
        issues: null,
        ok: false,
      },
    ]
  }

  const prosperity = readinessItem(r, 'prosperity')
  const enrollment = readinessItem(r, 'enrollment')
  const prep = readinessItem(r, 'prep')

  // Without API/client readiness, do not assume Enrollment is healthy.
  if (!r) {
    return [
      {
        key: 'prosperity',
        text: 'Prosperity · review',
        tone: 'warn',
        issues: null,
        ok: false,
      },
      {
        key: 'enrollment',
        text: 'Enrollment · review',
        tone: 'warn',
        issues: null,
        ok: false,
      },
      {
        key: 'prep',
        text: 'Departure · review',
        tone: 'warn',
        issues: null,
        ok: false,
      },
    ]
  }

  const prosperityPill = pillFromItem(
    prosperity,
    'Prosperity ✓',
    'Prosperity',
    'Prosperity',
  )
  const enrollmentPill = pillFromItem(
    enrollment,
    'Enrollment ✓',
    'Enrollment',
    'Enrollment',
  )
  let prepPill
  if (!prep) {
    prepPill = {
      text: 'Departure · review',
      tone: 'warn',
      issues: null,
      ok: false,
    }
  } else if (prep.ok === true || prep.complete === true) {
    prepPill = { text: 'Departure ✓', tone: 'ok', issues: 0, ok: true }
  } else {
    const n = prep.issues ?? prep.count ?? prep.missing
    prepPill = {
      text:
        n != null && Number(n) > 0
          ? `Departure · ${n} issue${Number(n) === 1 ? '' : 's'}`
          : 'Departure · issues',
      tone: 'warn',
      issues: n != null ? Number(n) : null,
      ok: false,
    }
  }

  return [
    { key: 'prosperity', ...prosperityPill },
    { key: 'enrollment', ...enrollmentPill },
    { key: 'prep', ...prepPill },
  ]
}

export const getParticipantCount = (quest) => {
  const count =
    quest?.participantsCount ??
    quest?.participantCount ??
    quest?.totalParticipants ??
    quest?.participants?.length
  if (count == null) {
    return null
  }
  const n = Number(count)
  return Number.isNaN(n) ? null : n
}

export const formatExperienceDateRange = (quest) => {
  const { start, end } = getProgramDatesRange(quest)
  const startLabel = start ? UTCFormatDateShort(start) : null
  const endLabel = end ? UTCFormatDateShort(end) : null
  if (startLabel && endLabel) {
    return `${startLabel} – ${endLabel}`
  }
  return startLabel || endLabel || null
}

export const formatStartsLong = (questOrDate) => {
  const start =
    typeof questOrDate === 'string' || questOrDate instanceof Date
      ? questOrDate
      : questOrDate?.programDates?.questStartDate ||
        getProgramDatesRange(questOrDate).start
  if (!start) {
    return null
  }
  const d = new Date(start)
  if (Number.isNaN(d.getTime())) {
    return UTCFormatDateShort(start)
  }
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

export const formatUSD = (amount) => {
  if (amount == null || Number.isNaN(Number(amount))) {
    return '—'
  }
  const n = Number(amount)
  return `$${Math.round(n).toLocaleString('en-US')}`
}

export const pluralize = (count, singular, plural) => {
  const n = Number(count) || 0
  return n === 1 ? singular : plural
}

/**
 * Server missingField keys → copy + Build navigation target.
 * Keep in sync with quest readiness field keys on the server.
 */
export const OVERVIEW_FIELDS = {
  programDates: {
    title: 'Programme dates',
    small: 'Needed for duration, lifecycle status and the investment card.',
    stage: 'setup',
    target: 'setup-dates',
  },
  title: {
    title: 'Public title',
    small: 'Shown on the Prosperity page and in participant communications.',
    stage: 'setup',
    target: 'setup-name',
  },
  description: {
    title: 'Summary',
    small: 'Short description that introduces the experience.',
    stage: 'setup',
    target: 'setup-name',
  },
  standardRate: {
    title: 'Shared room rate',
    small: 'The investment card would be incomplete.',
    stage: 'setup',
    target: 'setup-price',
  },
  imageFileName: {
    title: 'Cover image',
    small: 'Needed for participant view and admin list thumbnail.',
    stage: 'setup',
    target: 'setup-cover',
  },
  headerImage: {
    title: 'Slider images',
    small: 'At least one Prosperity page hero image is required.',
    stage: 'sell',
    target: 'content-hero',
  },
  inclusions: {
    title: 'Inclusions',
    small: 'Finish the Prosperity page inclusions before publishing.',
    stage: 'sell',
    target: 'content-inclusions',
  },
  faculties: {
    title: 'Faculty',
    small: 'Add at least one visible faculty member.',
    stage: 'sell',
    target: 'content-faculty',
  },
  journeySections: {
    title: 'Your Journey',
    small: 'Finish the Prosperity page narrative before launch.',
    stage: 'sell',
    target: 'content-journey',
  },
  faqs: {
    title: 'FAQs',
    small: 'Add at least one FAQ for the Prosperity page.',
    stage: 'sell',
    target: 'content-faqs',
  },
  inquiryCustomForm: {
    title: 'Express Interest questions',
    small: 'Interest capture needs at least one active question.',
    stage: 'sell',
    target: 'content-interest',
  },
  signupCopy: {
    title: 'Signup page copy',
    small: 'Enrollment signup messaging is incomplete.',
    stage: 'enrol',
    target: 'enrol-signup',
  },
  welcomeEmail: {
    title: 'Welcome email',
    small: 'Welcome email template needs attention.',
    stage: 'enrol',
    target: 'enrol-email',
  },
  enrollmentCustomForm: {
    title: 'Enrollment form questions',
    small: 'Enrollment form needs at least one active question.',
    stage: 'enrol',
    target: 'enrol-form',
  },
}

const KNOWN_OVERVIEW_FIELD_KEYS = Object.keys(OVERVIEW_FIELDS)

/** Dev-time guard so new server keys cannot render as raw identifiers. */
export const warnUnknownOverviewFieldKeys = (keys = []) => {
  if (process.env.NODE_ENV === 'production') {
    return
  }
  keys.forEach((key) => {
    if (!OVERVIEW_FIELDS[key]) {
      // eslint-disable-next-line no-console
      console.error(
        `[overview] Unknown missingField key "${key}". Add it to OVERVIEW_FIELDS.`,
      )
    }
  })
}

export const resolveOverviewField = (key) => {
  const meta = OVERVIEW_FIELDS[key]
  if (!meta) {
    warnUnknownOverviewFieldKeys([key])
    return {
      title: key,
      small: 'Required before publishing.',
      stage: 'setup',
      target: 'setup-name',
    }
  }
  return meta
}

/** Flatten prosperity + enrollment missingFields in server order. */
export const getOverviewMissingFieldKeys = (overview) => {
  const prosperity = overview?.readiness?.prosperity?.missingFields || []
  const enrollment = overview?.readiness?.enrollment?.missingFields || []
  const keys = [...prosperity, ...enrollment]
  warnUnknownOverviewFieldKeys(keys)
  return keys
}

export const getOverviewPublishBlockers = (overview) =>
  getOverviewMissingFieldKeys(overview).map((key) => resolveOverviewField(key))

/** Short labels for draft readiness card subcopy (matches v7 HTML). */
const SHORT_FIELD_LABELS = {
  standardRate: 'Price',
  singleOccupancyRate: 'Price',
  imageFileName: 'cover image',
  headerImage: 'cover image',
  journeySections: 'journey',
  welcomeEmail: 'Welcome email',
  enrollmentCustomForm: 'form',
  signupCopy: 'signup',
}

const summarizeMissingFields = (keys = [], fallback) => {
  if (!keys.length) {
    return fallback
  }
  const labels = keys
    .map((key) => SHORT_FIELD_LABELS[key] || resolveOverviewField(key).title)
    .filter(Boolean)
  const unique = [...new Set(labels)].slice(0, 3)
  if (!unique.length) {
    return fallback
  }
  if (unique.length === 1) {
    return `${unique[0]} still needs work`
  }
  if (unique.length === 2) {
    return `${unique[0]} and ${unique[1]} still need work`
  }
  return `${unique[0]}, ${unique[1]} and ${unique[2]} still need work`
}

/**
 * Departure-prep readiness card.
 * Uses readiness.prep when present (overview or quest); otherwise scenario defaults.
 */
const buildPrepCard = (scenario, prepItem) => {
  if (scenario === 'copied') {
    return {
      key: 'prep',
      label: 'Dates & leaders',
      kind: 'needs',
      icon: '!',
      title: 'Needs a pass',
      small: 'Change anything that should be unique to this experience.',
      cta: 'Fix',
      stage: 'setup',
      target: 'setup-dates',
    }
  }
  if (scenario === 'new' || scenario === 'draft') {
    return {
      key: 'prep',
      label: 'Departure prep',
      kind: 'neutral',
      icon: '—',
      title: 'Later',
      small:
        scenario === 'new'
          ? 'No need to prepare participant logistics yet'
          : 'Does not block publishing',
      cta: scenario === 'new' ? 'Open' : 'Review',
      stage: 'travel',
    }
  }

  const issues = prepItem?.issues != null ? Number(prepItem.issues) : null
  const ok = prepItem?.ok === true || prepItem?.complete === true
  const isLive = scenario === 'live'

  if (ok) {
    return {
      key: 'prep',
      label: 'Departure prep',
      kind: 'good',
      icon: '✓',
      title: 'Ready',
      small: 'Checklist, itinerary and orientation are set',
      cta: 'Review',
      stage: 'travel',
    }
  }

  if (issues != null && issues > 0) {
    return {
      key: 'prep',
      label: 'Departure prep',
      kind: 'needs',
      icon: String(issues),
      title: isLive
        ? 'Needs attention'
        : `${issues} issue${issues === 1 ? '' : 's'}`,
      small: isLive
        ? 'Checklist + itinerary days outstanding'
        : 'Checklist, itinerary and orientation need work',
      cta: 'Fix',
      stage: 'travel',
    }
  }

  return {
    key: 'prep',
    label: 'Departure prep',
    kind: 'needs',
    icon: '·',
    title: isLive ? 'Needs attention' : 'In progress',
    small: 'Review checklist, itinerary and orientation',
    cta: 'Review',
    stage: 'travel',
  }
}

/**
 * Readiness cards for Overview from GET /quests/:id/overview.
 */
export const buildOverviewReadinessCards = (
  overview,
  scenario,
  quest = null,
) => {
  if (scenario === 'completed') {
    return null
  }
  if (scenario === 'copied') {
    return [
      {
        key: 'prosperity',
        label: 'Prosperity page',
        kind: 'good',
        icon: '✓',
        title: 'Copied',
        small:
          'Prosperity page content is in place. Confirm it still fits this run.',
        cta: 'Review',
        stage: 'sell',
      },
      {
        key: 'enrollment',
        label: 'Enrollment',
        kind: 'good',
        icon: '✓',
        title: 'Copied',
        small: 'Signup, welcome email and form were copied.',
        cta: 'Review',
        stage: 'enrol',
      },
      buildPrepCard(scenario),
    ]
  }
  if (scenario === 'new') {
    return [
      {
        key: 'prosperity',
        label: 'Prosperity page',
        kind: 'blocked',
        icon: '0',
        title: 'Not started',
        small: '0 required sections complete',
        cta: 'Start',
        stage: 'setup',
      },
      {
        key: 'enrollment',
        label: 'Enrollment',
        kind: 'neutral',
        icon: '—',
        title: 'Later',
        small: 'Configure after the Prosperity page basics are taking shape',
        cta: 'Open',
        stage: 'enrol',
      },
      buildPrepCard(scenario),
    ]
  }

  const experience = overview?.experience || {}
  const readiness = resolveReadiness(quest, overview?.readiness)
  const prosperity = readiness?.prosperity || overview?.readiness?.prosperity
  const enrollment = readiness?.enrollment || overview?.readiness?.enrollment
  const prep = readiness?.prep
  const isDraft = scenario === 'draft'
  const isLive = scenario === 'live'
  const isHidden = experience.isVisible === false && !!experience.isPublished

  const sectionCard = (key, label, item, { okTitle, okSmall, stage }) => {
    const issues = item?.issues != null ? Number(item.issues) : null
    const ok = item?.ok === true
    const firstMissing = item?.missingFields?.[0]
    const firstField = firstMissing ? resolveOverviewField(firstMissing) : null
    if (ok) {
      return {
        key,
        label,
        kind: 'good',
        icon: '✓',
        title: isHidden && key === 'prosperity' ? 'Hidden' : okTitle,
        small:
          isHidden && key === 'prosperity'
            ? 'Published but not visible on the public site'
            : okSmall,
        cta: 'Review',
        stage,
      }
    }
    if (isDraft) {
      // v7: Prosperity blockers are red; enrollment blockers are amber.
      return {
        key,
        label,
        kind: key === 'prosperity' ? 'blocked' : 'needs',
        icon: issues != null ? String(issues) : '!',
        title:
          issues != null
            ? `${issues} blocker${issues === 1 ? '' : 's'}`
            : 'Needs work',
        small: summarizeMissingFields(
          item?.missingFields,
          key === 'prosperity'
            ? 'Finish required Prosperity page content before publishing'
            : 'Welcome email and form are incomplete',
        ),
        cta: 'Fix',
        stage: firstField?.stage || (key === 'prosperity' ? 'setup' : 'enrol'),
        target: firstField?.target,
      }
    }
    return {
      key,
      label,
      kind: 'needs',
      icon: issues != null ? String(issues) : '!',
      title:
        issues != null
          ? `${issues} issue${issues === 1 ? '' : 's'}`
          : 'Needs attention',
      small: 'Review and fix remaining items',
      cta: 'Fix',
      stage: firstField?.stage || stage,
      target: firstField?.target,
    }
  }

  return [
    sectionCard('prosperity', 'Prosperity page', prosperity, {
      okTitle: isDraft ? 'Ready' : 'Live',
      okSmall: 'Required content is complete',
      stage: 'sell',
    }),
    sectionCard('enrollment', 'Enrollment', enrollment, {
      okTitle: isDraft || isLive ? 'Ready' : 'Open',
      okSmall: isLive
        ? 'Signup, email and form complete'
        : 'Signup, welcome email and form are ready',
      stage: 'enrol',
    }),
    buildPrepCard(scenario, prep),
  ]
}

/**
 * Attention rows from overview.attention + summary (copy built on FE).
 * Keys: documentsMissing | formUnsubmitted | balanceOutstanding
 */
/**
 * Participant-prep rows (checklist, itinerary, orientation, guide pairing).
 * `prep` comes from the Build rail (see ExperienceBuild onClientReadinessChange).
 */
const OVERVIEW_PREP_FIELD_TO_ITEM = {
  travelChecklist: 'prep-checklist',
  itinerary: 'prep-schedule',
  orientation: 'prep-orientation',
  orientationCall: 'prep-orientation',
}

// Overview API reports prep gaps as readiness.prep.missingFields; used until
// the Build rail (fresher, client-side) has loaded.
const prepFromOverview = (overviewPrep) => {
  const fields = Array.isArray(overviewPrep?.missingFields)
    ? overviewPrep.missingFields
    : []
  const itemIssues = {}
  fields.forEach((f) => {
    const item = OVERVIEW_PREP_FIELD_TO_ITEM[f]
    if (item) itemIssues[item] = true
  })
  return { itemIssues }
}

const buildPrepAttentionRows = (prepFromBuild, summary, overviewPrep) => {
  const prep = prepFromBuild || prepFromOverview(overviewPrep)
  const issues = prep.itemIssues || {}
  const total = Number(summary?.totalParticipants) || 0
  const outstanding = Number(summary?.outstandingPeopleCount) || 0
  const paid = Math.max(0, total - outstanding)
  const tone = paid > 0 ? 'danger' : 'warn'
  const rows = []
  if (issues['prep-checklist']) {
    rows.push({
      key: 'prep-checklist',
      tone,
      title: 'Travel checklist is empty',
      detail:
        paid > 0
          ? `${paid} fully-paid ${pluralize(
              paid,
              'participant',
              'participants',
            )} can already access this area.`
          : 'It opens as soon as someone’s balance clears.',
      cta: 'Fix →',
      open: { mode: 'build', stage: 'travel', target: 'prep-checklist' },
    })
  }
  if (issues['prep-schedule']) {
    const n = Number(prep.emptyItineraryDays) || 0
    rows.push({
      key: 'prep-schedule',
      tone,
      title:
        n > 0
          ? `${n} itinerary ${pluralize(n, 'day has', 'days have')} no schedule`
          : 'Itinerary has not been set up',
      detail:
        paid > 0
          ? 'Paid participants can already open the itinerary.'
          : 'These are the dated days in Participant prep, not Your Journey on the Prosperity page.',
      cta: 'Open →',
      open: { mode: 'build', stage: 'travel', target: 'prep-schedule' },
    })
  }
  if (issues['prep-orientation']) {
    rows.push({
      key: 'prep-orientation',
      tone: 'warn',
      title: 'Orientation call is not set',
      detail:
        'Participants already see this item. Add a link and date, or turn the step off.',
      cta: 'Set →',
      open: { mode: 'build', stage: 'travel', target: 'prep-orientation' },
    })
  }
  const unpaired = Number(prep.unpairedCount) || 0
  if (unpaired > 0) {
    rows.push({
      key: 'prep-guide-pairing',
      tone: 'warn',
      title: `${unpaired} ${pluralize(
        unpaired,
        'participant has',
        'participants have',
      )} no wisdom guide`,
      detail:
        'Pair interested people, then assign a SOW. Skip anyone who declined 1:1 coaching.',
      cta: 'Open →',
      open: { mode: 'build', stage: 'travel', target: 'prep-guide-pairing' },
    })
  }
  return rows
}

export const buildOverviewAttentionRows = (overview, scenario, prep = null) => {
  const summary = overview?.summary
  const attention = Array.isArray(overview?.attention) ? overview.attention : []
  const prepRows = buildPrepAttentionRows(
    prep,
    summary,
    overview?.readiness?.prep,
  )
  if (
    (attention.length === 0 && prepRows.length === 0) ||
    scenario === 'completed' ||
    scenario === 'draft' ||
    scenario === 'new' ||
    scenario === 'copied'
  ) {
    return []
  }

  const sorted = [...attention].sort((a, b) => {
    if (a.severity === b.severity) return 0
    if (a.severity === 'danger') return -1
    if (b.severity === 'danger') return 1
    return 0
  })

  const rosterRows = sorted.map((item) => {
    const tone = item.severity === 'danger' ? 'danger' : 'warn'
    if (item.key === 'documentsMissing') {
      const people = Number(summary?.documentsMissingPeopleCount) || 0
      const files = Number(summary?.documentsMissingCount) || 0
      return {
        key: item.key,
        tone,
        title: `${people} ${pluralize(
          people,
          'person is',
          'people are',
        )} missing documents`,
        detail: `${files} ${pluralize(
          files,
          'file is',
          'files are',
        )} still needed.`,
        cta: 'Review →',
        open: { mode: 'manage', target: 'manage-participants' },
      }
    }
    if (item.key === 'formUnsubmitted') {
      const total = Number(summary?.totalParticipants) || 0
      const enrolled = Number(summary?.enrolledCount) || 0
      const people = Math.max(0, total - enrolled)
      return {
        key: item.key,
        tone,
        title: `${people} ${pluralize(
          people,
          'person has',
          'people have',
        )} not submitted the enrollment form`,
        detail: 'They cannot reach prep until this is done.',
        cta: 'Review →',
        open: { mode: 'manage', target: 'manage-participants' },
      }
    }
    if (item.key === 'balanceOutstanding') {
      const people = Number(summary?.outstandingPeopleCount) || 0
      return {
        key: item.key,
        tone,
        title: `${people} ${pluralize(
          people,
          'person has',
          'people have',
        )} a remaining balance`,
        detail: `${formatUSD(summary?.outstandingAmount)} still to collect.`,
        cta: 'Review →',
        open: { mode: 'manage', target: 'manage-payments' },
      }
    }
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(`[overview] Unknown attention key "${item.key}".`)
    }
    return {
      key: item.key,
      tone,
      title: item.key,
      detail: 'Open Manage to review.',
      cta: 'Review →',
      open: { mode: 'manage', target: 'manage-participants' },
    }
  })
  return [...prepRows, ...rosterRows]
}

export const getOverviewProgramDates = (overview, quest) => {
  const start =
    overview?.experience?.programDates?.questStartDate ||
    getProgramDatesRange(quest).start
  const end =
    overview?.experience?.programDates?.questEndDate ||
    getProgramDatesRange(quest).end
  return { start, end }
}

/** Max places for Capacity / "of N places" — groupSize from overview or quest. */
export const getOverviewCapacity = (overview, quest) => {
  const raw =
    overview?.experience?.groupSize ??
    overview?.summary?.capacity ??
    overview?.summary?.groupSize ??
    quest?.groupSize
  const n = Number(raw)
  if (Number.isNaN(n) || n <= 0) {
    return null
  }
  return n
}

/** Relative last-edited label for draft overview head (v7: "last edited today"). */
export const formatLastEditedLabel = (dateValue) => {
  if (!dateValue) {
    return null
  }
  const d = new Date(dateValue)
  if (Number.isNaN(d.getTime())) {
    return null
  }
  const now = new Date()
  const startOfToday = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  )
  const startOfThatDay = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
  )
  const dayDiff = Math.round(
    (startOfToday - startOfThatDay) / (24 * 60 * 60 * 1000),
  )
  if (dayDiff <= 0) {
    return 'today'
  }
  if (dayDiff === 1) {
    return 'yesterday'
  }
  if (dayDiff < 7) {
    return `${dayDiff} days ago`
  }
  return UTCFormatDateShort(dateValue)
}

/** Assert every known server key is mapped (dev-time). */
export const assertOverviewFieldsCoverage = () => {
  if (process.env.NODE_ENV === 'production') {
    return
  }
  const expected = [
    'programDates',
    'title',
    'description',
    'standardRate',
    'imageFileName',
    'headerImage',
    'inclusions',
    'faculties',
    'journeySections',
    'faqs',
    'inquiryCustomForm',
    'signupCopy',
    'welcomeEmail',
    'enrollmentCustomForm',
  ]
  expected.forEach((key) => {
    if (!KNOWN_OVERVIEW_FIELD_KEYS.includes(key)) {
      // eslint-disable-next-line no-console
      console.error(`[overview] OVERVIEW_FIELDS missing expected key "${key}".`)
    }
  })
}

assertOverviewFieldsCoverage()
